import * as uuid from "uuid"
import { arrayToRecordById } from "../../../common/src/arrays"
import { rerouteConnection } from "../../../common/src/connection-utils"
import {
    Board,
    Color,
    Connection,
    Container,
    Item,
    Note,
    NoteShape,
    newContainer,
    newNote,
} from "../../../common/src/domain"
import { Coordinates as Point } from "../../../common/src/geometry"

// Import of the Mermaid flowchart subset (graph/flowchart TD|TB|BT|LR|RL) as native board items:
// nodes -> notes (shape by bracket type, colors from classDef/class), edges -> connections
// (with labels), subgraphs -> Areas laid out as clusters like mermaid does.
// ponytail: hand parser + layered layout, no mermaid/dagre deps; sequence/gantt etc. are out of scope.

type Dir = "TB" | "BT" | "LR" | "RL"
type NodeStyle = { fill?: string; color?: string; stroke?: string }
type PNode = {
    id: string
    text: string
    shape: NoteShape
    subgraph: string | null
    className: string | null
    w: number
    h: number
    x: number
    y: number
}
type PEdge = { from: string; to: string; label?: string; arrow: boolean }

export function isMermaidFlowchart(text: string): boolean {
    const first = text
        .split("\n")
        .map((l) => l.trim())
        .find((l) => l && !l.startsWith("%%"))
    return !!first && /^(graph|flowchart)\s+(TB|TD|BT|LR|RL)\b/.test(first)
}

const SKIP = /^(style\s|linkStyle|click\s|accTitle|accDescr)/

const NODE_RE = /^([\w.-]+)\s*(\(\([^)]*\)\)|\([^)]*\)|\[\[[^\]]*\]\]|\[\/[^\]]*\/\]|\[\\[^\]]*\\\]|\[[^\]]*\]|\{\{[^}]*\}\}|\{[^}]*\}|>[^\]]*\])?(?::::([\w-]+))?/
// "-- text -->" style first, then plain connectors with optional |label|
const EDGE_MID_RE = /^\s*(?:--|==|-\.)\s+(.+?)\s+(?:--+>|==+>|\.-+>|--+|==+|\.-+)\s*/
const EDGE_RE = /^\s*(-\.+->|-\.+-|--+[>xo]|--+|==+>|==+)\s*(?:\|([^|]*)\|)?\s*/

function cleanText(raw: string): string {
    return raw
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/^["'`]+|["'`]+$/g, "")
        .trim()
}

// Node text keeps <br> so notes get real line breaks (labels/titles stay plain via cleanText)
function cleanNodeText(raw: string): string {
    return raw
        .replace(/<br\s*\/?>/gi, "<br>")
        .replace(/^["'`]+|["'`]+$/g, "")
        .trim()
}
const textLines = (text: string) => text.split("<br>").map((l) => l.trim())

function shapeOf(bracket: string | undefined): NoteShape {
    if (!bracket) return "rect"
    if (bracket.startsWith("((")) return "round"
    if (bracket.startsWith("{")) return "diamond"
    return "rect"
}

export function parseMermaidFlowchart(
    text: string,
): {
    dir: Dir
    nodes: PNode[]
    edges: PEdge[]
    subgraphs: Map<string, string>
    subgraphDirs: Map<string, Dir>
    classes: Map<string, NodeStyle>
} | null {
    const lines = text
        .split("\n")
        .flatMap((l) => l.split(";"))
        .map((l) => l.replace(/%%.*$/, "").trim())
        .filter((l) => l)
    const header = lines.shift()
    const dirMatch = header?.match(/^(?:graph|flowchart)\s+(TB|TD|BT|LR|RL)\b/)
    if (!dirMatch) return null
    const dir: Dir = dirMatch[1] === "TD" ? "TB" : (dirMatch[1] as Dir)

    const nodes = new Map<string, PNode>()
    const edges: PEdge[] = []
    const subgraphs = new Map<string, string>() // id -> title
    const subgraphDirs = new Map<string, Dir>() // id -> internal direction override
    const classes = new Map<string, NodeStyle>() // class name -> style
    const subgraphStack: string[] = []

    const ensureNode = (id: string, bracket?: string, className?: string) => {
        let n = nodes.get(id)
        if (!n) {
            n = {
                id,
                text: id,
                shape: "rect",
                subgraph: subgraphStack[0] ?? null,
                className: null,
                w: 0,
                h: 0,
                x: 0,
                y: 0,
            }
            nodes.set(id, n)
        }
        if (bracket) {
            n.text = cleanNodeText(
                bracket
                    .replace(/^[[({>]+|[\])}]+$/g, "")
                    .replace(/^\/|\/$/g, "")
                    .replace(/^\\|\\$/g, ""),
            )
            n.shape = shapeOf(bracket)
        }
        if (className) n.className = className
        return n
    }

    for (const line of lines) {
        if (SKIP.test(line)) continue
        const classDef = line.match(/^classDef\s+([\w-]+)\s+(.+)$/)
        if (classDef) {
            const style: NodeStyle = {}
            for (const part of classDef[2].split(",")) {
                const [k, v] = part.split(":").map((x) => x.trim())
                if (k === "fill") style.fill = v
                if (k === "color") style.color = v
                if (k === "stroke") style.stroke = v
            }
            classes.set(classDef[1], style)
            continue
        }
        const classAssign = line.match(/^class\s+([\w.,\s-]+?)\s+([\w-]+)$/)
        if (classAssign) {
            for (const id of classAssign[1].split(",").map((x) => x.trim())) {
                const n = nodes.get(id)
                if (n) n.className = classAssign[2]
            }
            continue
        }
        const dirLine = line.match(/^direction\s+(TB|TD|BT|LR|RL)$/)
        if (dirLine) {
            if (subgraphStack[0]) subgraphDirs.set(subgraphStack[0], dirLine[1] === "TD" ? "TB" : (dirLine[1] as Dir))
            continue
        }
        const sub = line.match(/^subgraph\s+(.+)$/)
        if (sub) {
            const titled = sub[1].match(/^([\w.-]+)\s*\[(.+)\]$/)
            const id = titled ? titled[1] : sub[1].trim()
            subgraphs.set(id, cleanText(titled ? titled[2] : sub[1]))
            subgraphStack.unshift(id)
            continue
        }
        if (/^end$/.test(line)) {
            subgraphStack.shift()
            continue
        }
        // statement: nodegroup (edge nodegroup)*
        let rest = line
        const parseGroup = (): string[] | null => {
            const ids: string[] = []
            for (;;) {
                const m = rest.match(NODE_RE)
                if (!m || !m[1]) return ids.length ? ids : null
                ensureNode(m[1], m[2], m[3])
                ids.push(m[1])
                rest = rest.slice(m[0].length)
                const amp = rest.match(/^\s*&\s*/)
                if (!amp) return ids
                rest = rest.slice(amp[0].length)
            }
        }
        let prev = parseGroup()
        if (!prev) continue
        for (;;) {
            let label: string | undefined
            let arrow = true
            const mid = rest.match(EDGE_MID_RE)
            if (mid) {
                label = cleanText(mid[1])
                arrow = /[>]\s*$/.test(mid[0].trimEnd())
                rest = rest.slice(mid[0].length)
            } else {
                const e = rest.match(EDGE_RE)
                if (!e) break
                arrow = /[>xo]/.test(e[1])
                label = e[2] !== undefined ? cleanText(e[2]) : undefined
                rest = rest.slice(e[0].length)
            }
            const next = parseGroup()
            if (!next) break
            for (const a of prev) for (const b of next) edges.push({ from: a, to: b, label, arrow })
            prev = next
        }
    }
    if (nodes.size === 0) return null
    return { dir, nodes: [...nodes.values()], edges, subgraphs, subgraphDirs, classes }
}

const GAP = 3 // em between nodes

// Generic layered layout: ranks by longest path (back edges from cycles excluded),
// barycenter ordering within ranks, centered cross-axis. Returns top-left positions.
function layeredLayout(
    items: { id: string; w: number; h: number }[],
    edges: { from: string; to: string }[],
    vertical: boolean,
    flip: boolean,
    gap: number,
): Map<string, { x: number; y: number }> {
    const byId = new Map(items.map((n) => [n.id, n]))
    const ranks = new Map<string, number>()
    const order = new Map<string, number>()
    items.forEach((n, i) => {
        ranks.set(n.id, 0)
        order.set(n.id, i)
    })
    // back edges via DFS
    const out = new Map<string, { from: string; to: string }[]>()
    for (const e of edges) {
        if (!byId.has(e.from) || !byId.has(e.to)) continue
        if (!out.has(e.from)) out.set(e.from, [])
        out.get(e.from)!.push(e)
    }
    const state = new Map<string, "active" | "done">()
    const backEdges = new Set<{ from: string; to: string }>()
    const dfs = (id: string) => {
        state.set(id, "active")
        for (const e of out.get(id) ?? []) {
            const st = state.get(e.to)
            if (st === "active") backEdges.add(e)
            else if (!st) dfs(e.to)
        }
        state.set(id, "done")
    }
    for (const n of items) if (!state.has(n.id)) dfs(n.id)
    for (let pass = 0; pass < items.length; pass++) {
        let changed = false
        for (const e of edges) {
            if (backEdges.has(e) || !byId.has(e.from) || !byId.has(e.to) || e.from === e.to) continue
            if (ranks.get(e.to)! < ranks.get(e.from)! + 1) {
                ranks.set(e.to, ranks.get(e.from)! + 1)
                changed = true
            }
        }
        if (!changed) break
    }
    const rankLists = new Map<number, string[]>()
    for (const n of items) {
        const r = ranks.get(n.id)!
        if (!rankLists.has(r)) rankLists.set(r, [])
        rankLists.get(r)!.push(n.id)
    }
    for (let pass = 0; pass < 2; pass++) {
        for (const [, ids] of [...rankLists.entries()].sort((a, b) => a[0] - b[0])) {
            for (const id of ids) {
                const parents = edges.filter((e) => e.to === id && byId.has(e.from)).map((e) => order.get(e.from)!)
                if (parents.length) order.set(id, parents.reduce((s, p) => s + p, 0) / parents.length)
            }
            ids.sort((a, b) => order.get(a)! - order.get(b)!).forEach((id, i) => order.set(id, i))
        }
    }
    const positions = new Map<string, { x: number; y: number }>()
    const rankKeys = [...rankLists.keys()].sort((a, b) => a - b)
    let main = 0
    for (const r of rankKeys) {
        const ids = rankLists.get(r)!.sort((a, b) => order.get(a)! - order.get(b)!)
        const rankMainSize = Math.max(...ids.map((id) => (vertical ? byId.get(id)!.h : byId.get(id)!.w)))
        const totalCross = ids.reduce((s, id) => s + (vertical ? byId.get(id)!.w : byId.get(id)!.h) + gap, -gap)
        let cross = -totalCross / 2
        for (const id of ids) {
            const n = byId.get(id)!
            const size = vertical ? n.w : n.h
            positions.set(
                id,
                vertical
                    ? { x: cross, y: main + (rankMainSize - n.h) / 2 }
                    : { x: main + (rankMainSize - n.w) / 2, y: cross },
            )
            cross += size + gap
        }
        main += rankMainSize + gap * 1.5
    }
    if (flip) {
        const maxMain = Math.max(
            ...items.map((n) => (vertical ? positions.get(n.id)!.y + n.h : positions.get(n.id)!.x + n.w)),
        )
        for (const n of items) {
            const p = positions.get(n.id)!
            if (vertical) p.y = maxMain - p.y - n.h
            else p.x = maxMain - p.x - n.w
        }
    }
    return positions
}

// Area paddings around subgraph contents (title bar on top)
const CLUSTER_PAD_X = 1.5
const CLUSTER_PAD_TOP = 3
const CLUSTER_PAD_BOTTOM = 1.5

function layout(
    nodes: PNode[],
    edges: PEdge[],
    dir: Dir,
    subgraphs: Map<string, string>,
    subgraphDirs: Map<string, Dir>,
) {
    for (const n of nodes) {
        const lines = textLines(n.text)
        const len = Math.max(...lines.map((l) => l.length))
        const lineCount = lines.length
        if (n.shape === "diamond") {
            n.w = Math.min(18, Math.max(8, len * 0.5 + 5))
            n.h = Math.max(n.w * 0.6, lineCount * 1.6 + 3)
        } else if (n.shape === "round") {
            n.w = Math.min(12, Math.max(6, len * 0.5 + 4, lineCount * 1.4 + 3))
            n.h = n.w
        } else {
            n.w = Math.min(22, Math.max(7, len * 0.5 + 3))
            n.h = Math.max(4, lineCount * 1.4 + 1.6)
        }
    }
    const vertical = dir === "TB" || dir === "BT"
    const flip = dir === "BT" || dir === "RL"
    const byId = new Map(nodes.map((n) => [n.id, n]))

    if (subgraphs.size === 0) {
        const pos = layeredLayout(nodes, edges, vertical, flip, GAP)
        for (const n of nodes) {
            const p = pos.get(n.id)!
            n.x = p.x
            n.y = p.y
        }
        return
    }

    // cluster layout: lay out each subgraph internally, then arrange clusters (and solo nodes)
    // with the same algorithm at the coarse level, like mermaid/dagre clusters
    const groupOf = (n: PNode) => n.subgraph ?? "@" + n.id
    const clusters = new Map<string, PNode[]>()
    for (const n of nodes) {
        const g = groupOf(n)
        if (!clusters.has(g)) clusters.set(g, [])
        clusters.get(g)!.push(n)
    }
    const clusterSize = new Map<string, { w: number; h: number }>()
    const relPos = new Map<string, Map<string, { x: number; y: number }>>()
    for (const [gid, members] of clusters) {
        const internal = edges.filter(
            (e) =>
                byId.has(e.from) &&
                byId.has(e.to) &&
                groupOf(byId.get(e.from)!) === gid &&
                groupOf(byId.get(e.to)!) === gid,
        )
        const clusterDir = subgraphDirs.get(gid) ?? dir
        const pos = layeredLayout(
            members,
            internal,
            clusterDir === "TB" || clusterDir === "BT",
            clusterDir === "BT" || clusterDir === "RL",
            GAP,
        )
        // normalize to 0,0
        const minX = Math.min(...members.map((n) => pos.get(n.id)!.x))
        const minY = Math.min(...members.map((n) => pos.get(n.id)!.y))
        for (const n of members) {
            const p = pos.get(n.id)!
            p.x -= minX
            p.y -= minY
        }
        const w = Math.max(...members.map((n) => pos.get(n.id)!.x + n.w))
        const h = Math.max(...members.map((n) => pos.get(n.id)!.y + n.h))
        const isReal = subgraphs.has(gid)
        clusterSize.set(gid, {
            w: w + (isReal ? 2 * CLUSTER_PAD_X : 0),
            h: h + (isReal ? CLUSTER_PAD_TOP + CLUSTER_PAD_BOTTOM : 0),
        })
        relPos.set(gid, pos)
    }
    const coarseEdges: { from: string; to: string }[] = []
    const seen = new Set<string>()
    for (const e of edges) {
        const a = byId.get(e.from)
        const b = byId.get(e.to)
        if (!a || !b) continue
        const ga = groupOf(a)
        const gb = groupOf(b)
        if (ga === gb) continue
        const key = ga + ">" + gb
        if (!seen.has(key)) {
            seen.add(key)
            coarseEdges.push({ from: ga, to: gb })
        }
    }
    const coarseItems = [...clusterSize.entries()].map(([id, s]) => ({ id, w: s.w, h: s.h }))
    const coarsePos = layeredLayout(coarseItems, coarseEdges, vertical, flip, GAP * 1.5)
    for (const [gid, members] of clusters) {
        const base = coarsePos.get(gid)!
        const isReal = subgraphs.has(gid)
        for (const n of members) {
            const rel = relPos.get(gid)!.get(n.id)!
            n.x = base.x + rel.x + (isReal ? CLUSTER_PAD_X : 0)
            n.y = base.y + rel.y + (isReal ? CLUSTER_PAD_TOP : 0)
        }
    }
}

const NODE_COLOR = "#ffffff"

export function mermaidToItems(
    text: string,
    center: Point,
    crdt: any,
): { items: Item[]; connections: Connection[] } | null {
    const parsed = parseMermaidFlowchart(text)
    if (!parsed) return null
    const { dir, nodes, edges, subgraphs, subgraphDirs, classes } = parsed
    layout(nodes, edges, dir, subgraphs, subgraphDirs)

    // shift so the diagram is centered at the paste point (clamped to positive coords)
    const minX = Math.min(...nodes.map((n) => n.x))
    const minY = Math.min(...nodes.map((n) => n.y))
    const maxX = Math.max(...nodes.map((n) => n.x + n.w))
    const maxY = Math.max(...nodes.map((n) => n.y + n.h))
    // keep everything (including Area padding) at positive board coordinates
    const dx = Math.max(2 + CLUSTER_PAD_X - minX, center.x - (minX + maxX) / 2)
    const dy = Math.max(2 + CLUSTER_PAD_TOP - minY, center.y - (minY + maxY) / 2)

    const items: Item[] = []
    const noteIds = new Map<string, string>()
    const containerIds = new Map<string, string>()

    // subgraphs first (lower z), sized around member layout
    for (const [sgId, title] of subgraphs) {
        const members = nodes.filter((n) => n.subgraph === sgId)
        if (!members.length) continue
        const bx = Math.min(...members.map((n) => n.x)) - CLUSTER_PAD_X
        const by = Math.min(...members.map((n) => n.y)) - CLUSTER_PAD_TOP
        const bw = Math.max(...members.map((n) => n.x + n.w)) + CLUSTER_PAD_X - bx
        const bh = Math.max(...members.map((n) => n.y + n.h)) + CLUSTER_PAD_BOTTOM - by
        const container: Container = {
            ...newContainer(crdt),
            text: title,
            x: bx + dx,
            y: by + dy,
            width: bw,
            height: bh,
            z: 0,
        }
        items.push(container)
        containerIds.set(sgId, container.id)
    }
    for (const n of nodes) {
        const style = (n.className && classes.get(n.className)) || {}
        const note: Note = {
            ...newNote(n.text, (style.fill as Color) || NODE_COLOR, n.x + dx, n.y + dy, n.w, n.h, n.shape, 1),
            containerId: n.subgraph ? containerIds.get(n.subgraph) : undefined,
            ...(style.color ? { textColor: style.color as Color } : {}),
            ...(style.stroke ? { borderColor: style.stroke as Color } : {}),
        }
        items.push(note)
        noteIds.set(n.id, note.id)
    }
    // Route through rerouteConnection exactly like the Connect tool does: nearest attachment
    // sides plus a midpoint control point, so the connectors are selectable and bendable.
    const fakeBoard = ({ items: arrayToRecordById(items), connections: [] } as unknown) as Board
    const connections: Connection[] = edges.map((e) =>
        rerouteConnection(
            {
                id: uuid.v4(),
                from: noteIds.get(e.from)!,
                to: noteIds.get(e.to)!,
                controlPoints: [{ x: 0, y: 0 }],
                action: "connect",
                fromStyle: "none",
                toStyle: e.arrow ? "arrow" : "none",
                pointStyle: "black-dot",
                locked: false,
                ...(e.label ? { label: e.label } : {}),
            },
            fakeBoard,
        ),
    )
    routeAroundObstacles(connections, items)
    return { items, connections }
}

// Bow connection curves around boxes they would otherwise cut through.
// ponytail: single-control-point heuristic (our connections render one quadratic control),
// upgrade to dummy-node polylines if this stops being enough.
function segIntersectsRect(p1: Point, p2: Point, r: { x: number; y: number; width: number; height: number }): boolean {
    // Liang-Barsky clipping
    let t0 = 0
    let t1 = 1
    const dx = p2.x - p1.x
    const dy = p2.y - p1.y
    const clip = (p: number, q: number) => {
        if (p === 0) return q >= 0
        const t = q / p
        if (p < 0) {
            if (t > t1) return false
            if (t > t0) t0 = t
        } else {
            if (t < t0) return false
            if (t < t1) t1 = t
        }
        return true
    }
    return (
        clip(-dx, p1.x - r.x) &&
        clip(dx, r.x + r.width - p1.x) &&
        clip(-dy, p1.y - r.y) &&
        clip(dy, r.y + r.height - p1.y)
    )
}

function routeAroundObstacles(connections: Connection[], items: Item[]) {
    const notes = items.filter((i) => i.type === "note")
    const byId = new Map(items.map((i) => [i.id, i]))
    const PAD = 0.7
    for (const c of connections) {
        if (!c.controlPoints.length) continue
        const fromId = typeof c.from === "string" ? c.from : (c.from as any).id
        const toId = typeof c.to === "string" ? c.to : (c.to as any).id
        const a = byId.get(fromId)
        const b = byId.get(toId)
        if (!a || !b) continue
        const p1 = { x: a.x + a.width / 2, y: a.y + a.height / 2 }
        const p2 = { x: b.x + b.width / 2, y: b.y + b.height / 2 }
        const obstacles = notes
            .filter((n) => n.id !== fromId && n.id !== toId)
            .map((n) => ({ x: n.x - PAD, y: n.y - PAD, width: n.width + 2 * PAD, height: n.height + 2 * PAD }))
        const blocked = (m: Point) => obstacles.some((o) => segIntersectsRect(p1, m, o) || segIntersectsRect(m, p2, o))
        const mid = c.controlPoints[0]
        if (!blocked(mid)) continue
        const len = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1
        const perp = { x: -(p2.y - p1.y) / len, y: (p2.x - p1.x) / len }
        for (const d of [4, -4, 7, -7, 10, -10, 14, -14]) {
            const cand = { x: mid.x + perp.x * d, y: mid.y + perp.y * d }
            if (!blocked(cand)) {
                c.controlPoints = [cand]
                break
            }
        }
    }
}
